import { useCallback, useState } from 'react';

import { useOrgEmployeeRequestsApi } from '@shared/services';
import { useOrgEmployeeStore, selectOrgEmployee } from '@store/orgEmployee';
import { useRequestStore } from '@features/requests/stores/request';
import { toEmployeeRequest, toLegacyRequest } from '../utils';
import type { Request } from '@features/requests/types/requestTypes';

/**
 * Hook for sending comments on a request.
 * Uses PUT /api/v1/org-employee/{orgEmployeeId}/request/{requestId}/comment.
 */
export function useRequestComments(requestId: string | undefined): {
    isSending: boolean;
    error: string | null;
    addComment: (text: string) => Promise<boolean>;
} {
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { addComment: addCommentApi } = useOrgEmployeeRequestsApi();

    const addComment = useCallback(
        async (text: string): Promise<boolean> => {
            const trimmed = text.trim();

            if (!trimmed) {
                setError('Kommentar cannot be empty');
                return false;
            }

            if (!requestId) {
                setError('Request ID is required');
                return false;
            }

            const orgEmployee = selectOrgEmployee(
                useOrgEmployeeStore.getState(),
            );
            const orgEmployeeId = orgEmployee?.id;

            if (!orgEmployeeId) {
                setError('Org employee ID is required');
                return false;
            }

            setError(null);
            setIsSending(true);

            try {
                const response = await addCommentApi(
                    orgEmployeeId,
                    requestId,
                    trimmed,
                );

                // Update the store with the fresh request from the response
                try {
                    const employeeRequest = toEmployeeRequest(response);
                    const legacyRequest = toLegacyRequest(employeeRequest);

                    const currentRequests = useRequestStore.getState().requests;
                    const updatedRequests = currentRequests.map(
                        (r: Request): Request =>
                            r.id === requestId ? legacyRequest : r,
                    );

                    useRequestStore.getState().setRequests(updatedRequests);
                } catch {
                    // Store update failed, but the comment was already sent
                }

                setIsSending(false);
                return true;
            } catch {
                setError('Feil ved sending av kommentar');
                setIsSending(false);
                return false;
            }
        },
        [addCommentApi, requestId],
    );

    return { isSending, error, addComment };
}
